import { Component, Input, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { BrIconComponent } from '../../../shared/ui/icon/icon.component';
import { MatBadgeModule } from '@angular/material/badge';
import { MatTooltipModule } from '@angular/material/tooltip';
import { AuthService } from '../../../services/auth.service';
import { LocalStorageService } from '../../../services/local-storage.service';
import { LEAD_VIEWS, LEADS_VIEW_STORAGE_KEY, LeadViewEntry, availableViews } from './leads-views';

/** One spelling for a path, so a trailing slash cannot make two. */
function normalizePath(path: string): string {
  return ('/' + path.replace(/^\/+/, '')).replace(/\/+$/, '') || '/';
}

/**
 * One segmented control spanning routed Leads collection presentations and
 * analysis. The URL is the view state: selecting an entry always navigates.
 *
 * Availability is resolved once, on init, from the live router config and the
 * account's permissions (see `leads-views.ts`). An entry whose route is not
 * registered is simply not rendered — never a dead or disabled tab.
 */
@Component({
  selector: 'app-leads-view-switcher',
  standalone: true,
  imports: [CommonModule, MatButtonToggleModule, BrIconComponent, MatBadgeModule, MatTooltipModule],
  template: `
    <ng-container *ngIf="quiet; else standard">
      <div *ngIf="views.length > 1" class="br-view-switch br-view-switch--quiet" role="group" aria-label="Leads view">
        <button type="button" class="br-view" *ngFor="let view of views; trackBy: trackById"
                [class.br-view--on]="activeId === view.id" [attr.aria-pressed]="activeId === view.id"
                [matTooltip]="view.note || ''" [matBadge]="badgeFor(view)"
                [matBadgeHidden]="badgeFor(view) === null" matBadgeSize="small"
                (click)="choose(view)">{{ view.label }}</button>
      </div>
    </ng-container>
    <ng-template #standard>
    <mat-button-toggle-group class="pvs" [value]="activeId" aria-label="Leads view"
                             *ngIf="views.length > 1">
      <mat-button-toggle *ngFor="let view of views; trackBy: trackById" [value]="view.id"
                         [matTooltip]="view.note || ''"
                         (change)="choose(view)">
        <br-icon [matBadge]="badgeFor(view)" [matBadgeHidden]="badgeFor(view) === null"
                  matBadgeSize="small" matBadgeColor="warn">{{ view.icon }}</br-icon>
        {{ view.label }}
      </mat-button-toggle>
    </mat-button-toggle-group>
    </ng-template>
  `,
  styles: [`
    .pvs { --mat-standard-button-toggle-height: 34px; }
    .pvs { font-size: var(--br-font-size-sm); }
    .pvs br-icon { margin-right: 4px; vertical-align: middle; }
  `],
})
export class LeadsViewSwitcherComponent implements OnInit {
  @Input() quiet = false;

  /**
   * Optional per-entry counts, keyed by entry id (3f §31: the badge is the
   * "there is something for you over there" signal). Nothing supplies these
   * yet — the Overview queue depth and the Nurture wake count belong to those
   * pages — so the hook is here and every badge is absent until one is bound.
   */
  @Input() badges: Record<string, number | null> = {};

  /** Optional registry override, for tests and for a second host later. */
  @Input() entries: LeadViewEntry[] = LEAD_VIEWS;

  /** The entries this account is actually offered. */
  views: LeadViewEntry[] = [];

  constructor(
    private router: Router,
    private auth: AuthService,
    private storage: LocalStorageService,
  ) {}

  ngOnInit(): void {
    this.views = availableViews(
      this.router.config, key => this.auth.hasPermission(key), this.entries);
  }

  /**
   * The selected tab.
   *
   * A collection route remains selected while one of its child record pages is
   * open beside it (`/leads/table/42`, for example).
   */
  get activeId(): string | null {
    const here = this.here();
    return this.views.find(view => {
      const route = normalizePath(view.route);
      return here === route || here.startsWith(`${route}/`);
    })?.id ?? null;
  }

  badgeFor(view: LeadViewEntry): number | null {
    const count = this.badges[view.id];
    return count != null && count > 0 ? count : null;
  }

  /**
   * Act on a tab.
   *
   * Navigation is deliberate even between List and Board: a refresh or copied
   * link must reconstruct the same page without consulting cached UI state.
   */
  async choose(view: LeadViewEntry): Promise<void> {
    const navigated = await this.router.navigateByUrl(view.route);
    // Other hosts (such as Roll-off) use their own registry and preferences.
    if (navigated && this.entries === LEAD_VIEWS) {
      this.storage.setItem(LEADS_VIEW_STORAGE_KEY, view.id);
    }
  }

  private here(): string {
    return normalizePath(this.router.url.split('?')[0]);
  }

  trackById = (_: number, view: LeadViewEntry): string => view.id;
}
